import Vue from 'vue';

/**
 * NXTGEN-7362 Phase C — client helpers for location-scoped user/location pickers.
 *
 * Cycle routes (when cycleId is set):
 *   POST /v3/financialclose/analysis/usersforlocations/:pid
 *   POST /v3/financialclose/analysis/actorlocations/:pid
 * Setup routes (no cycleId):
 *   POST /v2/financialclose/masters/checklist/usersforlocations
 *   POST /v2/financialclose/masters/checklist/actorlocations
 */

const USERS_FOR_LOCATIONS_CYCLE = '/v3/financialclose/analysis/usersforlocations/';
const USERS_FOR_LOCATIONS_SETUP = '/v2/financialclose/masters/checklist/usersforlocations';
const ACTOR_LOCATIONS_CYCLE = '/v3/financialclose/analysis/actorlocations/';
const ACTOR_LOCATIONS_SETUP = '/v2/financialclose/masters/checklist/actorlocations';

function getAxios(axiosClient) {
  return axiosClient || Vue.axios;
}

function normalizeIdList(ids) {
  return (ids || [])
    .map((id) => {
      if (id === undefined || id === null || id === '') return '';
      if (typeof id === 'object' && id._id != null) return String(id._id);
      return String(id);
    })
    .filter(Boolean);
}

function extractArrayData(response) {
  const payload = response?.data?.data;
  return Array.isArray(payload) ? payload : [];
}

/**
 * Users whose L3 set covers every posted location (intersection).
 * @param {{ axios?: object, locationIds?: Array, cycleId?: string }} opts
 * @returns {Promise<string[]>} allowed user id strings
 */
export async function fetchUsersForLocations({ axios: axiosClient, locationIds, cycleId } = {}) {
  const ids = normalizeIdList(locationIds);
  if (!ids.length) return [];

  const axios = getAxios(axiosClient);
  const url = cycleId
    ? `${USERS_FOR_LOCATIONS_CYCLE}${cycleId}`
    : USERS_FOR_LOCATIONS_SETUP;
  const response = await axios.post(url, { locationIds: ids });
  return extractArrayData(response)
    .map((user) => {
      if (user == null) return '';
      if (typeof user === 'object') return String(user._id ?? '');
      return String(user);
    })
    .filter(Boolean);
}

/**
 * New array of users whose `_id` is in allowedIds. Does not mutate `users`.
 * @param {Array<{ _id: * }>} users
 * @param {Array} allowedIds
 * @returns {Array}
 */
export function filterUsersToAllowedIds(users, allowedIds) {
  const allowed = new Set(normalizeIdList(allowedIds));
  return (users || []).filter((user) => allowed.has(String(user?._id ?? '')));
}

/**
 * Ids from selectedIds that remain in allowedIds. Does not mutate either list.
 * @param {Array} selectedIds
 * @param {Array} allowedIds
 * @returns {string[]}
 */
export function dropIdsNotAllowed(selectedIds, allowedIds) {
  const allowed = new Set(normalizeIdList(allowedIds));
  return normalizeIdList(selectedIds).filter((id) => allowed.has(id));
}

function locationRecordId(location) {
  if (location == null || location === '') return '';
  if (typeof location !== 'object') return String(location);
  return String(location._id || location.id || '');
}

/**
 * Scoped location options plus already-selected locations from the catalog.
 * Selected ids stay visible when they are outside the actor's location set.
 * @param {Array} scopedLocations
 * @param {Array} selectedIds
 * @param {Array} catalog
 * @returns {Array}
 */
export function mergeSelectedLocations(scopedLocations = [], selectedIds = [], catalog = []) {
  const scoped = Array.isArray(scopedLocations) ? scopedLocations : [];
  const scopedIds = new Set(scoped.map(locationRecordId).filter(Boolean));
  const catalogById = new Map();
  (Array.isArray(catalog) ? catalog : []).forEach((location) => {
    const id = locationRecordId(location);
    if (id) catalogById.set(id, location);
  });
  const extras = [];
  normalizeIdList(selectedIds).forEach((id) => {
    if (scopedIds.has(id)) return;
    const known = catalogById.get(id);
    const name = known?.name != null && String(known.name).trim()
      ? String(known.name)
      : id;
    extras.push({
      ...(known && typeof known === 'object' ? known : {}),
      _id: id,
      id,
      name,
    });
  });
  return [...scoped, ...extras];
}

/**
 * True when any selected id is missing from the actor's allowed locations.
 * @param {Array} selectedIds
 * @param {Array} allowedIds
 * @returns {boolean}
 */
export function hasLocationOutsideAccess(selectedIds, allowedIds) {
  const allowed = new Set(normalizeIdList(allowedIds));
  return normalizeIdList(selectedIds).some((id) => !allowed.has(id));
}

/**
 * Actor's enabled locations (optional cycle intersection).
 * @param {{ axios?: object, cycleId?: string }} opts
 * @returns {Promise<Array<{ _id: string, name: string }>>}
 */
export async function fetchActorLocations({ axios: axiosClient, cycleId } = {}) {
  const axios = getAxios(axiosClient);
  const url = cycleId
    ? `${ACTOR_LOCATIONS_CYCLE}${cycleId}`
    : ACTOR_LOCATIONS_SETUP;
  const response = await axios.post(url, {});
  return extractArrayData(response)
    .map((loc) => ({
      _id: String(loc?._id ?? ''),
      name: loc?.name != null ? String(loc.name) : '',
    }))
    .filter((loc) => loc._id);
}
